import { motion } from "framer-motion";
import { ScanSearch, Workflow, BarChart3, ArrowRight } from "lucide-react";

const phases = [
  {
    icon: ScanSearch,
    phase: "Phase 1",
    title: "Find the Gaps",
    desc: "We examine where buyer and seller opportunities may stall:",
    items: [
      "Website inquiries",
      "Calls",
      "Portal leads",
      "Social messages",
      "Open-house contacts",
      "Booking",
      "CRM workflow",
      "Follow-up",
    ],
  },
  {
    icon: Workflow,
    phase: "Phase 2",
    title: "Fix the Flow",
    desc: "We implement focused workflows that help:",
    items: [
      "Respond",
      "Qualify",
      "Follow up",
      "Nurture",
      "Book",
      "Remind",
      "Update the CRM",
      "Alert the agent when human attention is needed",
    ],
  },
  {
    icon: BarChart3,
    phase: "Phase 3",
    title: "Measure What Changed",
    desc: "We track:",
    items: [
      "Response time",
      "Contact rate",
      "Follow-up completion",
      "Appointments booked",
      "Recovered leads",
      "Pipeline opportunity influenced",
    ],
  },
];

export default function LeakageToClosed() {
  return (
    <section id="process" data-testid="leakage-section" className="py-20 md:py-28 bg-[#0D1220]">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            From Lead Leakage to <span className="text-gradient">Closed Opportunity</span>
          </h2>
          <p className="mt-4 text-lg md:text-xl text-slate-200 leading-relaxed">
            Every iVIBES engagement follows the same three phases — so you always know what we're
            looking at, what we're fixing, and how we'll know it worked.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {phases.map((p, i) => (
            <motion.div
              key={p.title}
              data-testid={`phase-card-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.12, duration: 0.5 }}
              className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-7 flex flex-col transition-[border-color,box-shadow] duration-300 hover:border-cyan-400/30 hover:shadow-[0_0_36px_rgba(34,211,238,0.08)]"
            >
              {i < phases.length - 1 && (
                <div
                  data-testid={`phase-connector-${i}`}
                  className="hidden md:flex absolute -right-[1.35rem] top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full border border-cyan-400/30 bg-[#0D1220] text-cyan-300 items-center justify-center"
                >
                  <ArrowRight size={14} strokeWidth={2.25} />
                </div>
              )}
              <div className="flex items-center gap-3 mb-5">
                <div className="h-11 w-11 shrink-0 rounded-xl bg-cyan-500/10 text-cyan-300 flex items-center justify-center">
                  <p.icon size={22} strokeWidth={1.75} />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-indigo-300">
                  {p.phase}
                </span>
              </div>
              <h3 className="font-heading font-bold text-2xl text-white mb-2">{p.title}</h3>
              <p className="text-slate-300 text-base leading-relaxed mb-5">{p.desc}</p>
              <ul className="flex flex-wrap gap-2 mt-auto" data-testid={`phase-items-${i}`}>
                {p.items.map((item) => (
                  <li
                    key={item}
                    className="rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-sm text-slate-200"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
